import { AlertCircle, CheckCircle, Loader2, XCircle } from 'lucide-react';
import * as React from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

import type { BulkOperationState } from './useBulkSOWOperations';

interface FirmBasic {
  id: string;
  name: string;
}

interface BulkOperationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  firms: FirmBasic[];
  changes: Map<string, 'enable' | 'disable'>;
  operationState: BulkOperationState;
  onConfirm: () => void;
  onDone: () => void;
}

export const BulkOperationDialog: React.FC<BulkOperationDialogProps> = ({
  open,
  onOpenChange,
  firms,
  changes,
  operationState,
  onConfirm,
  onDone,
}) => {
  const { status, currentFirmIndex, totalFirms, currentFirmName, results } =
    operationState;

  const enableChanges = Array.from(changes.entries()).filter(
    ([_, action]) => action === 'enable',
  );
  const disableChanges = Array.from(changes.entries()).filter(
    ([_, action]) => action === 'disable',
  );

  const successCount = results.filter((r) => r.status === 'success').length;
  const errorCount = results.filter((r) => r.status === 'error').length;
  const totalWorkflowsDisabled = results.reduce(
    (sum, r) => sum + r.workflowsDisabled,
    0,
  );
  const totalServicesUpdated = results.reduce(
    (sum, r) => sum + r.servicesUpdated,
    0,
  );

  const progress =
    totalFirms > 0 ? Math.round((currentFirmIndex / totalFirms) * 100) : 0;

  // Confirmation view
  const renderConfirmation = () => (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>Confirm Bulk Service Changes</AlertDialogTitle>
        <AlertDialogDescription asChild>
          <div className="space-y-4">
            <p>
              You are about to make the following changes to{' '}
              <span className="font-semibold text-zinc-900 dark:text-zinc-100">
                {firms.length} firm{firms.length !== 1 ? 's' : ''}
              </span>
              :
            </p>

            {enableChanges.length > 0 && (
              <div>
                <p className="text-sm font-medium text-green-700 dark:text-green-400 mb-1">
                  ENABLE:
                </p>
                <ul className="list-disc list-inside text-sm space-y-0.5">
                  {enableChanges.map(([name]) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              </div>
            )}

            {disableChanges.length > 0 && (
              <div>
                <p className="text-sm font-medium text-zinc-700 dark:text-zinc-400 mb-1">
                  DISABLE:
                </p>
                <ul className="list-disc list-inside text-sm space-y-0.5">
                  {disableChanges.map(([name]) => (
                    <li key={name}>
                      {name}{' '}
                      <span className="text-zinc-500">
                        (will disable workflows first)
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <p className="text-sm font-medium text-zinc-700 dark:text-zinc-400 mb-1">
                Affected firms:
              </p>
              <div className="max-h-32 overflow-auto bg-zinc-50 dark:bg-zinc-900 rounded p-2">
                <ul className="text-sm space-y-0.5">
                  {firms.slice(0, 10).map((firm) => (
                    <li
                      key={firm.id}
                      className="text-zinc-600 dark:text-zinc-400"
                    >
                      {firm.name}
                    </li>
                  ))}
                  {firms.length > 10 && (
                    <li className="text-zinc-500 italic">
                      ... and {firms.length - 10} more
                    </li>
                  )}
                </ul>
              </div>
            </div>

            <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-950/30 rounded text-amber-800 dark:text-amber-200">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <p className="text-sm">
                This operation cannot be undone automatically. You will need to
                manually revert any changes if needed.
              </p>
            </div>
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm}>Apply Changes</AlertDialogAction>
      </AlertDialogFooter>
    </>
  );

  // Processing view
  const renderProcessing = () => (
    <AlertDialogHeader>
      <AlertDialogTitle className="flex items-center gap-2">
        <Loader2 className="h-5 w-5 animate-spin" />
        Applying Changes...
      </AlertDialogTitle>
      <AlertDialogDescription asChild>
        <div className="space-y-4">
          <Progress value={progress} className="h-2" />

          <p className="text-sm">
            Processing: <span className="font-medium">{currentFirmName}</span> (
            {currentFirmIndex + 1} of {totalFirms})
          </p>

          {results.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-1">Completed:</p>
              <div className="max-h-32 overflow-auto bg-zinc-50 dark:bg-zinc-900 rounded p-2">
                <ul className="text-sm space-y-1">
                  {results.map((result) => (
                    <li
                      key={result.firmId}
                      className={cn(
                        'flex items-center gap-1',
                        result.status === 'success'
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400',
                      )}
                    >
                      {result.status === 'success' ? (
                        <CheckCircle className="h-3 w-3" />
                      ) : (
                        <XCircle className="h-3 w-3" />
                      )}
                      {result.firmName} - {result.servicesUpdated} service
                      {result.servicesUpdated !== 1 ? 's' : ''} updated
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </AlertDialogDescription>
    </AlertDialogHeader>
  );

  // Completed view
  const renderCompleted = () => (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle className="flex items-center gap-2">
          {errorCount === 0 ? (
            <>
              <CheckCircle className="h-5 w-5 text-green-500" />
              Operation Complete
            </>
          ) : (
            <>
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Operation Complete with Errors
            </>
          )}
        </AlertDialogTitle>
        <AlertDialogDescription asChild>
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-green-50 dark:bg-green-950/30 rounded p-2 text-center">
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {successCount}
                </p>
                <p className="text-xs text-green-700 dark:text-green-300">
                  Successful
                </p>
              </div>
              {errorCount > 0 && (
                <div className="bg-red-50 dark:bg-red-950/30 rounded p-2 text-center">
                  <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                    {errorCount}
                  </p>
                  <p className="text-xs text-red-700 dark:text-red-300">
                    Errors
                  </p>
                </div>
              )}
            </div>

            <div className="text-sm text-zinc-600 dark:text-zinc-400">
              <p>
                {totalServicesUpdated} service
                {totalServicesUpdated !== 1 ? 's' : ''} updated
              </p>
              {totalWorkflowsDisabled > 0 && (
                <p>
                  {totalWorkflowsDisabled} workflow
                  {totalWorkflowsDisabled !== 1 ? 's' : ''} disabled
                </p>
              )}
            </div>

            {/* Detailed results */}
            <div>
              <p className="text-sm font-medium mb-1">Details:</p>
              <div className="max-h-48 overflow-auto bg-zinc-50 dark:bg-zinc-900 rounded p-2">
                <ul className="text-sm space-y-1">
                  {results.map((result) => (
                    <li
                      key={result.firmId}
                      className={cn(
                        'flex items-start gap-1',
                        result.status === 'success'
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400',
                      )}
                    >
                      {result.status === 'success' ? (
                        <CheckCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      )}
                      <div>
                        <span className="font-medium">{result.firmName}</span>
                        {result.status === 'success' ? (
                          <span className="text-zinc-500">
                            {' '}
                            - {result.servicesUpdated} service
                            {result.servicesUpdated !== 1 ? 's' : ''}
                            {result.workflowsDisabled > 0 &&
                              `, ${result.workflowsDisabled} workflow${result.workflowsDisabled !== 1 ? 's' : ''}`}
                          </span>
                        ) : (
                          <span className="text-red-500">
                            {' '}
                            - {result.message}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogAction onClick={onDone}>Done</AlertDialogAction>
      </AlertDialogFooter>
    </>
  );

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg">
        {status === 'idle' || status === 'confirming'
          ? renderConfirmation()
          : status === 'processing'
            ? renderProcessing()
            : renderCompleted()}
      </AlertDialogContent>
    </AlertDialog>
  );
};
